import { Button, Col, ColProps, Container, Form, Input, Label, Row } from "reactstrap";
import styles from "./style.module.scss";
import { faArrowLeft, faArrowRight, faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { nanoid } from 'nanoid/non-secure';
import { CSSProperties, FormEvent, forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useState } from "react";
import cx from "classnames";
import LoadingScreen from "@components/content/LoadingScreen";
import { IconsProvider, Pagination, Theme, ThemeProvider } from "@aws-amplify/ui-react";
import { ApiClient, FetchResponse, Nullable } from "@/api/ApiClient";
import { ConfirmableButtonProps } from "@components/content/ConfirmableButton";
import { renderItemActions } from "@utils/ListUtils";
import Select from "react-select";
import { SelectOptionType } from "@utils/Utils";
import { CustomFieldsTemplateIds } from "@pages/user/CustomFields";
import { CustomFieldApi, CustomFieldDataType } from "@/api/CustomFieldsApi";

export const MEILI_SEARCH_ACTIVATE = true;// flag to activate MEILI search if needed

type HighlightedColorsProps = {
  bg: string,
  text: string
}

export type MessagesTypeProps = {
  noResults?: string,
  noFilteredResults?: string
}

type PrimaryProperty<T> = {
  key: keyof T | ((data: T) => string | ReactNode);
  alignment?: "Start" | "Center" | "End";
  className?: string | undefined;
  highlighted?: boolean;
  options?: Record<string, string>;
  optionalHighlighted?: (val: any, item: T) => string | boolean;
  highlightedColors?: Record<string, HighlightedColorsProps>
};

type SecondaryProperty<T> = {
  key: keyof T | ((data: T) => string | ReactNode);
  title?: string;
  alignment?: "Start" | "Center" | "End";
  highlighted?: boolean;
  options?: Record<string, string>;
  optionalHighlighted?: (val: any, item: T) => string;
  highlightedText?: string;
  nonHighlightedText?: string;
  highlightedColors?: Record<string, HighlightedColorsProps>
  featuredTitle?: boolean;
  featured?: boolean;
};

type FilterProperty<T> = {
  key: keyof T;
  filterType: "beginsWith" | "contains" | "eq" | "gt" | "le" | "ge" | "lt" | "ne" | "notContains";
  type?: "number" | "string"
  innerProp?: string
  placeholder?: string
  options?: () => Promise<Record<string, string>>
};

/**
 * Dato adicional que se pinta en una fila propia debajo de cada elemento, con el mismo
 * formato "Titulo: valor" que usa la informacion personalizada de los documentos. Las
 * entradas sin valor se omiten.
 */
export type ExtraInfoEntry = {
  title: string,
  value?: string | null
};

type ModelIndexProps<T extends { id?: string }> = {
  /**
   * Origen en Dynamo. Opcional porque un listado que solo existe como indice de Meili
   * (el reporte de documentos electronicos) no tiene un modelo Amplify al que caer.
   */
  fetchData?: (filters: any, token?: string, limit?: number) => Promise<FetchResponse<T>>;
  relatedFetchData?: (relatedId: string, filters: any, token?: string, limit?: number) => Promise<FetchResponse<T>>;
  fetchAdditionaItems?: (filterText: string) => Promise<FetchResponse<T>>;
  onDataFetched?: (items: T[]) => void;
  limit?: number,
  primaryProperties?: PrimaryProperty<T>[];
  primaryPropertiesSizing?: ColProps[];
  secondaryProperties?: SecondaryProperty<T>[];
  secondaryPropertiesSizing?: ColProps[];
  filterProps?: FilterProperty<T>[];
  includeCustomFields?: boolean
  customFieldsDocumentType?: CustomFieldsTemplateIds,
  extraInfoRow?: (item: T) => ExtraInfoEntry[];
  listActions?: ListActionProps<T>[];
  mandatoryFilters?: Record<string, any>[];
  messages?: MessagesTypeProps;
  meiliConfig?: Meiliconfig,
  children?: string | ReactNode
};

type Meiliconfig = {
  index: "" | "clients" | "invoices" | "products" | "providers" | "quotes" | "recibes" | "creditnotes" | "debitnotes" | "expenses" | "settlements" | "providerinvoices" | "buyinvoices" | "providerorders" | "edocuments",
  hitsPerPage?: number,
  filter?: string,
  sort?: string[]
}

type MeiliResult = Nullable<{
  hits: string,
  estimatedTotalHits: number,
  page: number,
  totalPages: number,
  hitsPerPage: number,
  processingTimeMs: number,
  query: string,
}>

type PaginationTokenMap = Record<number, string | undefined> | undefined | null;

export type ListActionProps<T> = {
  actionTitle: string
  isVisible?: (item: T) => boolean,
  onClick?: (id: string, item: T) => void,
  href?: string,
  target?: "_blank" | "_self"
  iconProps: FontAwesomeIconProps,
  needConfirm?: boolean
  confirmProps?: ConfirmableButtonProps
} & React.RefAttributes<HTMLAnchorElement>;

const theme: Theme = {
  name: 'pagination-theme',
  tokens: {
    components: {
      pagination: {
        current: {
          backgroundColor: { value: '#337ab7' },
        },
        button: {
          hover: {
            backgroundColor: { value: '#23527c' },
            color: { value: '#fff' },
          },
        },
      },
    },
  },
};

export type ModelIndexHandle = {
  refetch: () => Promise<void>;
};

function ModelIndexInner<T extends { id?: string }>(
  props: ModelIndexProps<T>,
  ref: React.Ref<ModelIndexHandle>
) {

  const {
    fetchData, relatedFetchData, fetchAdditionaItems, filterProps = [], messages = {}, onDataFetched,
    limit = 50, listActions = [], mandatoryFilters = [], includeCustomFields = false,
    secondaryProperties = [], secondaryPropertiesSizing: customSecondarySizing, customFieldsDocumentType,
    extraInfoRow,
    primaryProperties = [], primaryPropertiesSizing: customPrimarySizing, meiliConfig, children } = props;

  const modelSearchIndex = `cb-${nanoid(10)}`;
  const [items, setItems] = useState<T[]>([]);
  const [secondaryPropertiesSizing, setSecondaryPropertiesSizing] = useState<ColProps[]>([]);
  const [primaryPropertiesSizing, setPrimaryPropertiesSizing] = useState<ColProps[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterText, setFilterText] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [relativeValue, setRelativeValue] = useState<string>("");
  const [selectPlaceholder, setSelectPlaceholder] = useState<string>("");
  const [meiliResult, setMeiliResult] = useState<Partial<MeiliResult>>({});

  const [pageTokens, setPageTokens] = useState<PaginationTokenMap[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);
  const [customFields, setCustomFields] = useState<CustomFieldDataType>();
  const [hasMorePages, setHasMorePages] = useState(true);
  const [reFetchDone, setReFetchDone] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [relativeOptions, setRelativeOptions] = useState<SelectOptionType[]>([]);
  let isPaginationVisible = pageTokens.filter(p => !!p).length > 0 && !isLoading && items.length > 0
  const currentSelectOption = relativeOptions.find(o => o.value === relativeValue)
  const useMeiliIndex = MEILI_SEARCH_ACTIVATE && meiliConfig?.index

  if (useMeiliIndex) {
    isPaginationVisible = meiliResult.totalPages ? meiliResult.totalPages > 1 : false;
  }

  // Los filtros de Meili los controla la pagina (por ejemplo el tipo de documento en el
  // reporte de comprobantes electronicos), asi que un cambio tiene que volver a consultar
  // el indice. Se depende de valores primitivos y no del objeto `meiliConfig`, que las
  // paginas arman en linea y seria una referencia nueva en cada render (bucle de fetch).
  const meiliIndexName = meiliConfig?.index || "";
  const meiliFilter = meiliConfig?.filter || "";
  const meiliSortKey = (meiliConfig?.sort || []).join(",");

  useEffect(() => {
    setIsLoading(true)
    populateSelectPlaceHolder()
    // Al cambiar el filtro se vuelve a la primera pagina: la paginacion anterior ya no
    // corresponde al nuevo conjunto de resultados.
    fetchDataFromSource();
  }, [filterText, relativeValue, meiliIndexName, meiliFilter, meiliSortKey]);


  useEffect(() => {
    const relativeInput = filterProps.find(f => f.options);

    const run = async () => {
      if (relativeInput?.options) {
        const options = await relativeInput?.options();

        const selectOptions = Object.entries(options).map(([k, v]) => {
          return { value: k, label: v }
        }
        );
        setRelativeOptions(selectOptions)
      }
    }
    run();
  }, [filterProps]);

  useEffect(() => {
    const propCount = primaryProperties.length
    if (customPrimarySizing) {
      setPrimaryPropertiesSizing(customPrimarySizing);
    } else {
      switch (propCount) {
        case 1:
          setPrimaryPropertiesSizing([
            { xs: "2" }
          ])
          break
        case 2:
          setPrimaryPropertiesSizing([
            { xs: "2" },
            { xs: "10" },
          ])
          break
      }
    }
  }, [primaryProperties.length]);

  useEffect(() => {
    const propCount = secondaryProperties.length
    if (customSecondarySizing) {
      setSecondaryPropertiesSizing(customSecondarySizing);
    } else {
      switch (propCount) {
        case 2:
          setSecondaryPropertiesSizing([
            { xs: "3", sm: "4", md: "2" },
            { xs: "9", sm: "8", md: "5" }
          ])
          break
        case 3:
          setSecondaryPropertiesSizing([
            { sm: "4", md: "3" },
            { sm: "4", md: "3" },
            { sm: "4", md: "3" }
          ])
          break
        case 4:
          setSecondaryPropertiesSizing([
            { sm: "4", md: "2" },
            { sm: "8", md: "4" },
            { sm: "6", md: "2" },
            { sm: "6", md: "1" }
          ])
          break
      }
    }
  }, [secondaryProperties.length]);

  useEffect(() => {
    if (customFieldsDocumentType) {
      const loadCustomFields = async () => {
        const customFields = await CustomFieldApi.fetchByDocumentType(customFieldsDocumentType)
        setCustomFields(customFields)
      }
      loadCustomFields()
    }

  }, [customFieldsDocumentType]);

  useEffect(() => {
    if (initialFetchDone && reFetchDone && !isLoading && items.length == 0 && hasMorePages) {
      handleNextPage()
    }
  }, [items, hasMorePages, initialFetchDone, reFetchDone, isLoading]);

  const fetchDataFromSource = async () => {
    if (useMeiliIndex) {
      await fetchFromMeiliSearch(1)
    } else {
      await fetchFromDynamo()
    }
  }

  const fetchFromMeiliSearch = async (pageNumber: number) => {
    try {
      const result = await ApiClient.queries.searchGeneric({
        index: `${meiliConfig?.index}`,
        q: filterText,
        page: pageNumber,
        hitsPerPage: meiliConfig?.hitsPerPage || 30,
        sort: meiliConfig?.sort || [],
        filter: meiliConfig?.filter || ""
      })
      const items = JSON.parse(`${result.data?.hits}`)
      if (result.data) {
        const { hits, ...rest } = result.data
        setMeiliResult(rest);
      }
      setItems(items);
      if (onDataFetched) {
        onDataFetched(items)
      }

      setCurrentPageIndex(pageNumber)
    } finally {
      setIsLoading(false)
      if (!initialFetchDone) {
        setInitialFetchDone(true)
      }
    }
  }

  const fetchFromDynamo = async () => {
    try {
      let nextToken, items: T[], additionaItems: T[] = [];
      if (fetchAdditionaItems && filterText) {
        const { items } = await fetchAdditionaItems(filterText)
        additionaItems = items
      }
      if (relativeValue && relatedFetchData) {
        const data = await relatedFetchData(relativeValue, generateFilterCriteria(filterText), ``, limit);
        nextToken = data.nextToken
        items = data.items
      } else if (fetchData) {
        const data = await fetchData(generateFilterCriteria(filterText), ``, limit);
        nextToken = data.nextToken
        items = data.items
      } else {
        items = []
      }

      if (additionaItems) {
        const filterItems = additionaItems.filter(i => !items.find(fi => i.id == fi.id))
        items = [
          ...filterItems,
          ...items
        ]
      }

      setItems(items);
      if (onDataFetched) {
        onDataFetched(items)
      }

      if (!nextToken) {
        setHasMorePages(false);
      }

      setPageTokens([nextToken]);
      setCurrentPageIndex(1)
    } finally {
      setIsLoading(false)
      if (!initialFetchDone) {
        setInitialFetchDone(true)
      }
    }
  }

  const populateSelectPlaceHolder = () => {
    const relateOption = filterProps.find(p => p.options)

    if (relateOption) {
      setSelectPlaceholder(String(relateOption.placeholder))
    }
  }

  const updatePageContent = async (pageNumber: number): Promise<string> => {
    try {
      setIsLoading(true)
      const nextApiToken = `${pageNumber >= 0 ? (pageTokens[pageNumber] || '') : ''}`;
      let nextToken, items;
      if (relativeValue && relatedFetchData) {
        const data = await relatedFetchData(relativeValue, generateFilterCriteria(filterText), nextApiToken, limit);
        nextToken = data.nextToken
        items = data.items
      } else if (fetchData) {
        const data = await fetchData(generateFilterCriteria(filterText), nextApiToken, limit);
        nextToken = data.nextToken
        items = data.items
      } else {
        items = []
      }

      setItems(items);
      if (onDataFetched) {
        onDataFetched(items)
      }

      if (!nextToken) {
        setHasMorePages(false);
      }

      return `${nextToken}`
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setReFetchDone(true)
      });
    }
  }

  const refetch = useCallback(async () => {
    setReFetchDone(false)
    if (useMeiliIndex) {
      await fetchFromMeiliSearch(currentPageIndex || 1)
    } else {
      await updatePageContent(currentPageIndex);
    }
  }, [updatePageContent, currentPageIndex]);

  useImperativeHandle(ref, () => ({
    refetch
  }));

  const handleSetPage = (pageIndex?: number) => {
    if (pageIndex) {
      updatePageContent(pageIndex - 2)
      setCurrentPageIndex(pageIndex)
    }
  }

  const handlePrevPage = async () => {
    const newCurrentPageIndex = currentPageIndex - 1;
    if (newCurrentPageIndex >= 0) {
      updatePageContent(newCurrentPageIndex - 2)
    }

    setCurrentPageIndex(newCurrentPageIndex);
  }

  const handleNextPage = async () => {
    if (hasMorePages && currentPageIndex === pageTokens.length) {
      const nextToken = await updatePageContent(pageTokens.length - 1)

      setPageTokens([...pageTokens, nextToken]);
    }

    setCurrentPageIndex(currentPageIndex + 1);
  };

  const combineFilters = (mandatory: any[], fields: any): any => {
    if (mandatory.length === 0) return fields;

    const mandatoryBlock = mandatory.length === 1 ? mandatory[0] : { and: mandatory };

    if (!fields || (Array.isArray(fields) && fields.length === 0)) {
      return mandatoryBlock;
    }

    return {
      and: [
        mandatoryBlock,
        fields
      ]
    };
  };

  const generateFilterCriteria = (filterText: string): any => {
    const fieldFilters = generateFieldsFilterCriteria(filterText);
    return combineFilters(mandatoryFilters, fieldFilters);
  };

  const generateFieldsFilterCriteria = (filterText: string): any => {
    if (!filterText && !relativeValue) {
      return []
    }
    const filterObjects = generatePrimaryfilter(filterProps, filterText);
    const relateOption = filterProps.find(p => p.options)

    if (relativeValue && filterObjects.length) {
      const relatedPropertyKey = String(relateOption?.key);

      return {
        and: [
          {
            [relatedPropertyKey]: { eq: relativeValue }
          },
          {
            or: filterObjects
          }
        ]
      }

    } else {
      return filterObjects.length ? {
        or: filterObjects
      } : []

    }
  }

  const generatePrimaryfilter = (filterProps: FilterProperty<T>[], filterText: string) => {
    return filterProps.filter(p => !p.options).map(f => {
      const { type = "string", innerProp } = f;
      let value: string | number;

      if (type == "number") {
        const numericValue = Number(filterText);
        if (isNaN(numericValue)) {
          // if the datatype is numeric and the text is not a valid number ignore filter property
          return null
        } else {
          value = numericValue
        }
      } else {
        value = filterText
      }
      if (innerProp) {
        return {
          [f.key]: {
            [innerProp]: {
              [f.filterType]: value
            }
          }
        }
      } else {
        return {
          [f.key]: {
            [f.filterType]: value
          }
        }
      }
    }).filter(fo => !!fo)
  }

  const renderPrimaryProperties = (data: T): ReactNode[] => {
    return primaryProperties.map((properties, i) => {
      const {
        key, alignment = "Center", options, highlighted, className,
        highlightedColors, optionalHighlighted } = properties;

      let isHighlighted = highlighted ? 'true' : 'false'
      const sizing = primaryPropertiesSizing[i];

      let value
      if (key instanceof Function) {
        value = key(data)
      } else {
        value = `${data[key] || ''}`;
      }

      let customStyle: CSSProperties = {};

      if (options) {
        value = options[`${value}`];
      } else if (optionalHighlighted) {
        isHighlighted = optionalHighlighted(value, data)
      }

      if (highlightedColors) {
        const { bg, text } = highlightedColors[`${isHighlighted}`];
        if (bg) {
          customStyle["backgroundColor"] = bg
          customStyle["color"] = text
          customStyle["fontWeight"] = "bold"
        }
      }

      return (
        <Col {...sizing} className={cx([
          styles[`text${alignment}`],
          styles.ellipsis,
          styles.primaryProperty,
          className,
          (isHighlighted != "false" ? styles.highlighted : null)
        ])}
          style={customStyle}
        >
          <span>{`${value}`}</span>
        </Col>
      )
    });
  }

  const renderSecondaryProperties = (data: T): ReactNode[] => {

    return secondaryProperties.map((properties, i) => {
      const {
        key, title, alignment = "Center", options,
        highlighted, featuredTitle, featured, highlightedColors,
        optionalHighlighted, highlightedText, nonHighlightedText } = properties;

      let isHighlighted = highlighted ? 'true' : 'false'
      const sizing = secondaryPropertiesSizing[i];

      let value
      if (key instanceof Function) {
        value = key(data)
      } else {
        const attributeValue = `${data[key]}`
        value = `${attributeValue || ''}`;
      }

      let customStyle: CSSProperties = {};

      if (options) {
        value = options[`${value}`];
      } else if (optionalHighlighted) {
        isHighlighted = optionalHighlighted(value, data)
        if (highlightedText && nonHighlightedText) {
          value = `${(isHighlighted ? highlightedText : nonHighlightedText)}`
        }
      }

      if (highlightedColors) {
        const { bg, text } = highlightedColors[`${isHighlighted}`];
        if (bg) {
          customStyle["backgroundColor"] = bg
          customStyle["color"] = text
          customStyle["fontWeight"] = "bold"
        }
      }

      let propTitle = title ? (<span>{title}: </span>) : null;
      if (propTitle && featuredTitle) {
        propTitle = (<b>{propTitle}</b>)
      }

      const ValueWrapperTag = featured ? "b" : "span"

      return (
        <Col {...sizing} className={cx([
          styles[`text${alignment}`],
          styles.bordered,
          styles.ellipsis,
          (isHighlighted != "false" ? styles.highlighted : null)
        ])}
          style={customStyle}
        >
          <ValueWrapperTag>{propTitle}{typeof value == "object" ? value : `${value}`}</ValueWrapperTag>
        </Col>
      )
    });
  }

  const renderActions = (item: T): ReactNode[] => {
    return renderItemActions(item, listActions)

  }

  const getMessageText = (messageType: keyof MessagesTypeProps, defaultText: string) => {
    return messages[messageType] || defaultText;
  }

  const renderItems = (): ReactNode[] | ReactNode => {
    if (initialFetchDone && !isLoading && items.length == 0) {
      return filterText ?
        <b>{getMessageText("noFilteredResults", "No se encontraron resultados con los filtros aplicados.")}</b> :
        <b>{getMessageText("noResults", "No hay elementos disponibles por el momento.")}</b>
    } else {
      return items.map(i => {
        return (
          <div data-testid={`model-row-${(i as { id?: string }).id ?? ""}`}>
            <Row>
              {renderPrimaryProperties(i)}
            </Row >
            <Row className={styles.line}>
              {renderSecondaryProperties(i)}
              <Col className={styles.actions}>
                {renderActions(i)}
              </Col>
              {includeCustomFields && renderCustomFieldData(i)}
              {extraInfoRow && renderExtraInfoRow(i)}
            </Row >

          </div>
        )
      })
    }
  }

  function hasCustomfields(
    obj: any
  ): obj is { customfields: string } {
    return obj && "customfields" in obj;
  }

  type CustomFields = {
    showIndex: boolean,
    field_title: string
  }

  const renderCustomFieldData = (item: T): ReactNode => {
    if (hasCustomfields(item) && customFields) {

      try {
        const itemCustomProps: { title: string, value: string }[] = JSON.parse(item.customfields)
        const customProps = JSON.parse(`${customFields.fields}`)
        return (
          <Row noGutters>
            {customProps.filter((p: CustomFields) => p.showIndex).map((cf: CustomFields) => {
              const itemCustomProValue = itemCustomProps.find(icp => icp.title == cf.field_title);
              itemCustomProps

              return itemCustomProValue?.value && (
                <Col md={3} className={cx([
                  styles.textCenter,
                  styles.bordered
                ])}>
                  {cf.field_title}: {itemCustomProValue.value}
                </Col>
              )
            })}
          </Row>)
      } catch (err) {

      }
    }
    return null
  }

  /**
   * Misma presentacion que `renderCustomFieldData`: una fila propia bajo el elemento con
   * un `Col` por dato. Se reutiliza el formato para que la informacion opcional siempre
   * se vea igual en todos los listados.
   */
  const renderExtraInfoRow = (item: T): ReactNode => {
    const entries = (extraInfoRow?.(item) || []).filter(e => !!e.value);

    if (entries.length === 0) {
      return null;
    }

    return (
      <Row noGutters>
        {entries.map(entry => (
          <Col key={entry.title} md={3} className={cx([
            styles.textCenter,
            styles.bordered
          ])}>
            {entry.title}: {entry.value}
          </Col>
        ))}
      </Row>
    )
  }

  const search = (e: FormEvent) => {
    e.preventDefault();
    setFilterText(inputValue);
  }

  const clearSearchForm = () => {
    setInputValue("");
    setFilterText("");
    setRelativeValue("");
  }

  const renderFilterForm = (): ReactNode | null => {
    if (filterProps.length > 0) {
      return (
        <>
          <Form onSubmit={(e) => search(e)}>
            <Row className="row-cols-lg-auto g-1 align-items-center">
              <Col>
                <Label className="g-0 pe-3"
                  for={modelSearchIndex}
                >
                  Filtro de Busqueda
                </Label>
              </Col>
              {!useMeiliIndex && relativeOptions && relativeOptions.length > 0 && (<Col lg="3">
                <Select
                  name="relativeFilter"
                  placeholder={selectPlaceholder}
                  value={currentSelectOption || null}
                  options={relativeOptions}
                  onChange={(newValue) => { setRelativeValue(`${newValue?.value}`) }}
                />
              </Col>
              )}
              <Col lg="3">
                <Input
                  id={modelSearchIndex}
                  name="filter"
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </Col>
              <Col>
                <Button data-testid="model-search-submit" className="me-1" color="primary" type="submit">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </Button>
                <Button data-testid="model-search-clear" color="danger" type="button" onClick={() => clearSearchForm()}>
                  <FontAwesomeIcon icon={faXmark} />
                </Button>
              </Col>
            </Row>
          </Form>
          <hr />
        </>
      )
    }
    return null
  }

  const renderPagination = (): ReactNode => {
    if (useMeiliIndex) {
      return (<
        Pagination
        currentPage={meiliResult.page || 1}
        totalPages={meiliResult.totalPages || 1}
        onNext={() => fetchFromMeiliSearch(currentPageIndex + 1)}
        onPrevious={() => fetchFromMeiliSearch(currentPageIndex - 1)}
        onChange={(pageIndex) => fetchFromMeiliSearch(pageIndex || 1)}
        siblingCount={2}
      />)
    } else {
      return (
        <Pagination
          currentPage={currentPageIndex}
          totalPages={pageTokens.length}
          hasMorePages={hasMorePages}
          onNext={handleNextPage}
          onPrevious={handlePrevPage}
          onChange={(pageIndex) => handleSetPage(pageIndex)}
          siblingCount={2}
        />)
    }
  }

  return (
    <Container fluid>
      <Row>
        <Col
          className={styles.container}
          md="12"
        >
          {renderFilterForm()}
          {children}
          {renderItems()}
        </Col>
      </Row>
      {isPaginationVisible && (
        <Row>
          <Col>
            <ThemeProvider theme={theme} colorMode="light">
              <IconsProvider
                icons={{
                  pagination: {
                    next: <FontAwesomeIcon icon={faArrowRight}></FontAwesomeIcon>,
                    previous: <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon>,
                  },
                }}
              >
                {renderPagination()}
              </IconsProvider>
            </ThemeProvider>
          </Col>
        </Row>
      )}

      <LoadingScreen isLoading={isLoading} />
    </Container >
  );
}

const ModelIndex = forwardRef(ModelIndexInner) as <T extends { id?: string }>(
  props: ModelIndexProps<T> & { ref?: React.Ref<ModelIndexHandle> }
) => ReturnType<typeof ModelIndexInner>;

export default ModelIndex;
